// Copyright (C) 2022 LTTS
//
// SPDX-License-Identifier: MIT

/**
 * External API which should be used by for development
 * @module API
 */

function build() {
    const PluginRegistry = require('./plugins');
    const loggerStorage = require('./logger-storage');
    const Log = require('./log');
    const ObjectState = require('./object-state');
    const Statistics = require('./statistics');
    const Comment = require('./comment');
    const Issue = require('./issue');
    const { Job, Task } = require('./session');
    const { Project } = require('./project');
    const implementProject = require('./project-implementation');
    const { Attribute, Label } = require('./labels');
    const MLModel = require('./ml-model');
    const { FrameData } = require('./frames');
    const { CloudStorage } = require('./cloud-storage');
    const Organization = require('./organization');

    const enums = require('./enums');

    const { Exception, ArgumentError, DataError, ScriptingError, PluginError, ServerError } = require('./exceptions');

    const User = require('./user');
    const pjson = require('../package.json');
    const config = require('./config');

    /**
     * API entrypoint
     * @namespace ltts
     * @memberof module:API
     */
    const ltts = {
        /**
         * Namespace is used for an interaction with a server
         * @namespace server
         * @package
         * @memberof module:API.ltts
         */
        server: {
            /**
             * @typedef {Object} ServerInfo
             * @property {string} name A name of the tool
             * @property {string} description A description of the tool
             * @property {string} version A version of the tool
             * @global
             */

            /**
             * Method returns some information about the annotation tool
             * @method about
             * @async
             * @memberof module:API.ltts.server
             * @return {ServerInfo}
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async about() {
                const result = await PluginRegistry.apiWrapper(ltts.server.about);
                return result;
            },
            /**
             * @typedef {Object} FileInfo
             * @property {string} name A name of a file
             * @property {module:API.ltts.enums.ShareFileType} type
             * A type of a file
             * @global
             */

            /**
             * Method returns a list of files in a specified directory on a share
             * @method share
             * @async
             * @memberof module:API.ltts.server
             * @param {string} [directory=/] - Share directory path
             * @returns {FileInfo[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async share(directory = '/') {
                const result = await PluginRegistry.apiWrapper(ltts.server.share, directory);
                return result;
            },
            /**
             * Method returns available annotation formats
             * @method formats
             * @async
             * @memberof module:API.ltts.server
             * @returns {module:API.ltts.classes.AnnotationFormats}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async formats() {
                const result = await PluginRegistry.apiWrapper(ltts.server.formats);
                return result;
            },
            /**
             * Method returns user agreements that the user must accept
             * @method userAgreements
             * @async
             * @memberof module:API.ltts.server
             * @returns {Object[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async userAgreements() {
                const result = await PluginRegistry.apiWrapper(ltts.server.userAgreements);
                return result;
            },
            /**
             * Method allows to register on a server
             * @method register
             * @async
             * @memberof module:API.ltts.server
             * @param {string} username An username for the new account
             * @param {string} firstName A first name for the new account
             * @param {string} lastName A last name for the new account
             * @param {string} email A email address for the new account
             * @param {string} password1 A password for the new account
             * @param {string} password2 The confirmation password for the new account
             * @param {Object} userConfirmations An user confirmations of terms of use if needed
             * @returns {Object} response data
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async register(username, firstName, lastName, email, password1, password2, userConfirmations) {
                const result = await PluginRegistry.apiWrapper(
                    ltts.server.register,
                    username,
                    firstName,
                    lastName,
                    email,
                    password1,
                    password2,
                    userConfirmations,
                );
                return result;
            },
            /**
             * Method allows to login on a server
             * @method loginsso
             * @async
             * @memberof module:API.ltts.server
             * @param {string} email An email of an account
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async loginsso(email) {
                const result = await PluginRegistry.apiWrapper(ltts.server.loginsso, email);
                return result;
            },
            /**
             * Method allows to login on a server
             * @method loginssootp
             * @async
             * @memberof module:API.ltts.server
             * @param {string} otpnumer An email of an account
             * @param {string} email An email of an account
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async loginssootp(otpnumer, email) {
                const result = await PluginRegistry.apiWrapper(ltts.server.loginssootp, otpnumer, email);
                return result;
            },
            /**
             * Method allows to login on a server
             * @method login
             * @async
             * @memberof module:API.ltts.server
             * @param {string} username An username of an account
             * @param {string} password A password of an account
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async login(username, password) {
                const result = await PluginRegistry.apiWrapper(ltts.server.login, username, password);
                return result;
            },
            /**
             * Method allows to logout from the server
             * @method logout
             * @async
             * @memberof module:API.ltts.server
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async logout() {
                const result = await PluginRegistry.apiWrapper(ltts.server.logout);
                return result;
            },
            /**
             * Method allows to change user password
             * @method changePassword
             * @async
             * @memberof module:API.ltts.server
             * @param {string} oldPassword Current password for the account
             * @param {string} newPassword1 New password for the account
             * @param {string} newPassword2 Confirmation password for the account
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async changePassword(oldPassword, newPassword1, newPassword2) {
                const result = await PluginRegistry.apiWrapper(
                    ltts.server.changePassword,
                    oldPassword,
                    newPassword1,
                    newPassword2,
                );
                return result;
            },
            /**
             * Method allows to reset user password
             * @method requestPasswordReset
             * @async
             * @memberof module:API.ltts.server
             * @param {string} email A email address for the account
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async requestPasswordReset(email) {
                const result = await PluginRegistry.apiWrapper(ltts.server.requestPasswordReset, email);
                return result;
            },
            /**
             * Method allows to confirm reset user password
             * @method resetPassword
             * @async
             * @memberof module:API.ltts.server
             * @param {string} newPassword1 New password for the account
             * @param {string} newPassword2 Confirmation password for the account
             * @param {string} uid User id
             * @param {string} token Request authentication token
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async resetPassword(newPassword1, newPassword2, uid, token) {
                const result = await PluginRegistry.apiWrapper(
                    ltts.server.resetPassword,
                    newPassword1,
                    newPassword2,
                    uid,
                    token,
                );
                return result;
            },
            /**
             * Method allows to know whether you are authorized on the server
             * @method authorized
             * @async
             * @memberof module:API.ltts.server
             * @returns {boolean}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async authorized() {
                const result = await PluginRegistry.apiWrapper(ltts.server.authorized);
                return result;
            },
            /**
             * Method allows to do requests via ltts-core with authorization headers
             * @method request
             * @async
             * @memberof module:API.ltts.server
             * @param {string} url
             * @param {Object} data request parameters: method, headers, data, etc.
             * @returns {Object | undefined} response data if exist
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async request(url, data) {
                const result = await PluginRegistry.apiWrapper(ltts.server.request, url, data);
                return result;
            },

            /**
             * Method returns apps that are installed on the server
             * @method installedApps
             * @async
             * @memberof module:API.ltts.server
             * @returns {Object} map {installedApp: boolean}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async installedApps() {
                const result = await PluginRegistry.apiWrapper(ltts.server.installedApps);
                return result;
            },
        },
        /**
         * Namespace is used for getting projects
         * @namespace projects
         * @memberof module:API.ltts
         */
        projects: {
            /**
             * @typedef {Object} ProjectFilter
             * @property {string} name Check if name contains this value
             * @property {module:API.ltts.enums.ProjectStatus} status
             * Check if status contains this value
             * @property {integer} id Check if id equals this value
             * @property {integer} page Get specific page
             * (default REST API returns 20 projects per request.
             * In order to get more, it is need to specify next page)
             * @property {string} owner Check if owner user contains this value
             * @property {string} search Combined search of contains among all fields
             * @global
             */

            /**
             * Method returns list of projects corresponding to a filter
             * @method get
             * @async
             * @memberof module:API.ltts.projects
             * @param {ProjectFilter} [filter={}] project filter
             * @returns {module:API.ltts.classes.Project[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.get, filter);
                return result;
            },

            async getType(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.getType, filter);
                return result;
            },

            async getExportData(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.getExportData, filter);
                return result;
            },

            async getProjectData(id) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.getProjectData,id);
                return result;
            },

            async getExportDataFormat() {
                const result = await PluginRegistry.apiWrapper(ltts.projects.getExportDataFormat);
                return result;
            },

            async exportDataDownload(exportData) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.exportDataDownload, exportData);
                return result;
            },


            async getToken(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.getToken, filter);
                return result;
            },
            /**
             * Method returns list of project names with project ids
             * corresponding to a search phrase
             * used for autocomplete field
             * @method searchNames
             * @async
             * @memberof module:API.ltts.projects
             * @param {string} [search = ''] search phrase
             * @param {number} [limit = 10] number of returning project names
             * @returns {module:API.ltts.classes.Project[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             *
             */
            async searchNames(search = '', limit = 10) {
                const result = await PluginRegistry.apiWrapper(ltts.projects.searchNames, search, limit);
                return result;
            },
        },
        /**
         * Namespace is used for getting tasks
         * @namespace tasks
         * @memberof module:API.ltts
         */
        tasks: {
            /**
             * @typedef {Object} TaskFilter
             * @property {string} name Check if name contains this value
             * @property {module:API.ltts.enums.TaskStatus} status
             * Check if status contains this value
             * @property {module:API.ltts.enums.TaskMode} mode
             * Check if mode contains this value
             * @property {integer} id Check if id equals this value
             * @property {integer} page Get specific page
             * (default REST API returns 20 tasks per request.
             * In order to get more, it is need to specify next page)
             * @property {integer} projectId Check if project_id field contains this value
             * @property {string} owner Check if owner user contains this value
             * @property {string} assignee Check if assigneed contains this value
             * @property {string} search Combined search of contains among all fields
             * @global
             */

            /**
             * Method returns list of tasks corresponding to a filter
             * @method get
             * @async
             * @memberof module:API.ltts.tasks
             * @param {TaskFilter} [filter={}] task filter
             * @returns {module:API.ltts.classes.Task[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.tasks.get, filter);
                return result;
            },
        },
        /**
         * Namespace is used for getting jobs
         * @namespace jobs
         * @memberof module:API.ltts
         */
        jobs: {
            /**
             * @typedef {Object} JobFilter
             * Only one of fields is allowed simultaneously
             * @property {integer} taskID filter all jobs of specific task
             * @property {integer} jobID filter job with a specific id
             * @global
             */

            /**
             * Method returns list of jobs corresponding to a filter
             * @method get
             * @async
             * @memberof module:API.ltts.jobs
             * @param {JobFilter} filter job filter
             * @returns {module:API.ltts.classes.Job[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.jobs.get, filter);
                return result;
            },
        },
        /**
         * Namespace is used for getting users
         * @namespace users
         * @memberof module:API.ltts
         */
        users: {
            /**
             * @typedef {Object} UserFilter
             * @property {boolean} self get only self
             * @global
             */

            /**
             * Method returns list of users corresponding to a filter
             * @method get
             * @async
             * @memberof module:API.ltts.users
             * @param {UserFilter} [filter={}] user filter
             * @returns {module:API.ltts.classes.User[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.users.get, filter);
                return result;
            },
        },
        /**
         * Namespace is used for plugin management
         * @namespace plugins
         * @memberof module:API.ltts
         */
        plugins: {
            /**
             * @typedef {Object} Plugin
             * A plugin is a Javascript object. It must have properties are listed below. <br>
             * It also mustn't have property 'functions' which is used internally. <br>
             * You can expand any API method including class methods. <br>
             * In order to expand class method just use a class name
             * in a ltts space (example is listed below).
             *
             * @property {string} name A name of a plugin
             * @property {string} description A description of a plugin
             * Example plugin implementation listed below:
             * @example
             * plugin = {
             *   name: 'Example Plugin',
             *   description: 'This example plugin demonstrates how plugin system in CVAT works',
             *   ltts: {
             *     server: {
             *       about: {
             *         // Plugin adds some actions after executing the ltts.server.about()
             *         // For example it adds a field with installed plugins to a result
             *         // An argument "self" is a plugin itself
             *         // An argument "result" is a return value of ltts.server.about()
             *         // All next arguments are arguments of a wrapped function
             *         // (in this case the wrapped function doesn't have any arguments)
             *         async leave(self, result) {
             *           result.plugins = await self.internal.getPlugins();
             *           // Note that a method leave must return "result" (changed or not)
             *           // Otherwise API won't work as expected
             *           return result;
             *         },
             *       },
             *     },
             *     // In this example plugin also wraps a class method
             *     classes: {
             *       Job: {
             *         prototype: {
             *           annotations: {
             *             put: {
             *               // The first argument "self" is a plugin, like in a case above
             *               // The second argument is an argument of the
             *               // Job.annotations.put()
             *               // It contains an array of objects to put
             *               // In this sample we round objects coordinates and save them
             *               enter(self, objects) {
             *                 for (const obj of objects) {
             *                   if (obj.type != 'tag') {
             *                     const points = obj.position.map((point) => {
             *                       const roundPoint = {
             *                         x: Math.round(point.x),
             *                         y: Math.round(point.y),
             *                       };
             *                       return roundPoint;
             *                     });
             *                   }
             *                 }
             *               },
             *             },
             *           },
             *         },
             *       },
             *     },
             *   },
             *   // In general you can add any others members to your plugin
             *   // Members below are only examples
             *   internal: {
             *     async getPlugins() {
             *       // Collect information about installed plugins
             *       const plugins = await ltts.plugins.list();
             *       return plugins.map((el) => {
             *         return {
             *           name: el.name,
             *           description: el.description,
             *         };
             *       });
             *     },
             *   },
             * };
             * @global
             */

            /**
             * Method returns list of installed plugins
             * @method list
             * @async
             * @memberof module:API.ltts.plugins
             * @returns {Plugin[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async list() {
                const result = await PluginRegistry.apiWrapper(ltts.plugins.list);
                return result;
            },
            /**
             * Install plugin to CVAT
             * @method register
             * @async
             * @memberof module:API.ltts.plugins
             * @param {Plugin} [plugin] plugin for registration
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async register(plugin) {
                const result = await PluginRegistry.apiWrapper(ltts.plugins.register, plugin);
                return result;
            },
        },

        /**
         * Namespace is used for serverless functions management (mainly related with DL models)
         * @namespace lambda
         * @memberof module:API.ltts
         */
        lambda: {
            /**
             * Method returns list of available serverless models
             * @method list
             * @async
             * @memberof module:API.ltts.lambda
             * @returns {module:API.ltts.classes.MLModel[]}
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async list() {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.list);
                return result;
            },

            /**
             * Run long-time request for a function on a specific task
             * @method run
             * @async
             * @memberof module:API.ltts.lambda
             * @param {module:API.ltts.classes.Task} task task to be annotated
             * @param {module:API.ltts.classes.MLModel} model model used to get annotation
             * @param {object} [args] extra arguments
             * @returns {string} requestID
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ArgumentError}
             */
            async run(task, model, args) {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.run, task, model, args);
                return result;
            },

            /**
             * Run short-time request for a function on a specific task
             * @method call
             * @async
             * @memberof module:API.ltts.lambda
             * @param {module:API.ltts.classes.Task} task task to be annotated
             * @param {module:API.ltts.classes.MLModel} model model used to get annotation
             * @param {object} [args] extra arguments
             * @returns {object[]} annotations
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ArgumentError}
             */
            async call(task, model, args) {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.call, task, model, args);
                return result;
            },

            /**
             * Cancel running of a serverless function for a specific task
             * @method cancel
             * @async
             * @memberof module:API.ltts.lambda
             * @param {string} requestID
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ArgumentError}
             */
            async cancel(requestID) {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.cancel, requestID);
                return result;
            },

            /**
             * @callback onRequestStatusChange
             * @param {string} status
             * @param {number} progress
             * @param {string} [message]
             * @global
             */
            /**
             * Listen for a specific request
             * @method listen
             * @async
             * @memberof module:API.ltts.lambda
             * @param {string} requestID
             * @param {onRequestStatusChange} onChange
             * @throws {module:API.ltts.exceptions.ArgumentError}
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async listen(requestID, onChange) {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.listen, requestID, onChange);
                return result;
            },

            /**
             * Get active lambda requests
             * @method requests
             * @async
             * @memberof module:API.ltts.lambda
             * @throws {module:API.ltts.exceptions.ServerError}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async requests() {
                const result = await PluginRegistry.apiWrapper(ltts.lambda.requests);
                return result;
            },
        },
        /**
         * Namespace to working with logs
         * @namespace logger
         * @memberof module:API.ltts
         */
        /**
         * Method to logger configuration
         * @method configure
         * @memberof module:API.ltts.logger
         * @param {function} isActiveChecker - callback to know if logger
         * should increase working time or not
         * @param {object} userActivityCallback - container for a callback <br>
         * Logger put here a callback to update user activity timer <br>
         * You can call it outside
         * @instance
         * @async
         * @throws {module:API.ltts.exceptions.PluginError}
         * @throws {module:API.ltts.exceptions.ArgumentError}
         */

        /**
         * Append log to a log collection <br>
         * Durable logs will have been added after "close" method is called for them <br>
         * Ignore rules exist for some logs (e.g. zoomImage, changeAttribute) <br>
         * Payload of ignored logs are shallowly combined to previous logs of the same type
         * @method log
         * @memberof module:API.ltts.logger
         * @param {module:API.ltts.enums.LogType | string} type - log type
         * @param {Object} [payload = {}] - any other data that will be appended to the log
         * @param {boolean} [wait = false] - specifies if log is durable
         * @returns {module:API.ltts.classes.Log}
         * @instance
         * @async
         * @throws {module:API.ltts.exceptions.PluginError}
         * @throws {module:API.ltts.exceptions.ArgumentError}
         */

        /**
         * Save accumulated logs on a server
         * @method save
         * @memberof module:API.ltts.logger
         * @throws {module:API.ltts.exceptions.PluginError}
         * @throws {module:API.ltts.exceptions.ServerError}
         * @instance
         * @async
         */
        logger: loggerStorage,
        /**
         * Namespace contains some changeable configurations
         * @namespace config
         * @memberof module:API.ltts
         */
        config: {
            /**
             * @memberof module:API.ltts.config
             * @property {string} backendAPI host with a backend api
             * @memberof module:API.ltts.config
             * @property {string} proxy Axios proxy settings.
             * For more details please read <a href="https://github.com/axios/axios"> here </a>
             * @memberof module:API.ltts.config
             * @property {string} origin ui URL origin
             * @memberof module:API.ltts.config
             * @property {number} uploadChunkSize max size of one data request in mb
             * @memberof module:API.ltts.config
             */
            get backendAPI() {
                return config.backendAPI;
            },
            set backendAPI(value) {
                config.backendAPI = value;
            },
            get proxy() {
                return config.proxy;
            },
            set proxy(value) {
                config.proxy = value;
            },
            get origin() {
                return config.origin;
            },
            set origin(value) {
                config.origin = value;
            },
            get uploadChunkSize() {
                return config.uploadChunkSize;
            },
            set uploadChunkSize(value) {
                config.uploadChunkSize = value;
            },
        },
        /**
         * Namespace contains some library information e.g. api version
         * @namespace client
         * @memberof module:API.ltts
         */
        client: {
            /**
             * @property {string} version Client version.
             * Format: <b>{major}.{minor}.{patch}</b>
             * <li style="margin-left: 10px;"> A major number is changed after an API becomes
             * incompatible with a previous version
             * <li style="margin-left: 10px;"> A minor number is changed after an API expands
             * <li style="margin-left: 10px;"> A patch number is changed after an each build
             * @memberof module:API.ltts.client
             * @readonly
             */
            version: `${pjson.version}`,
        },
        /**
         * Namespace is used for access to enums
         * @namespace enums
         * @memberof module:API.ltts
         */
        enums,
        /**
         * Namespace is used for access to exceptions
         * @namespace exceptions
         * @memberof module:API.ltts
         */
        exceptions: {
            Exception,
            ArgumentError,
            DataError,
            ScriptingError,
            PluginError,
            ServerError,
        },
        /**
         * Namespace is used for getting cloud storages
         * @namespace cloudStorages
         * @memberof module:API.ltts
         */
        cloudStorages: {
            /**
             * @typedef {Object} CloudStorageFilter
             * @property {string} displayName Check if displayName contains this value
             * @property {string} resource Check if resource name contains this value
             * @property {module:API.ltts.enums.ProviderType} providerType Check if providerType equal this value
             * @property {integer} id Check if id equals this value
             * @property {integer} page Get specific page
             * (default REST API returns 20 clouds storages per request.
             * In order to get more, it is need to specify next page)
             * @property {string} owner Check if an owner name contains this value
             * @property {string} search Combined search of contains among all the fields
             * @global
             */

            /**
             * Method returns a list of cloud storages corresponding to a filter
             * @method get
             * @async
             * @memberof module:API.ltts.cloudStorages
             * @param {CloudStorageFilter} [filter={}] cloud storage filter
             * @returns {module:API.ltts.classes.CloudStorage[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get(filter = {}) {
                const result = await PluginRegistry.apiWrapper(ltts.cloudStorages.get, filter);
                return result;
            },
        },
        /**
         * This namespace could be used to get organizations list from the server
         * @namespace organizations
         * @memberof module:API.ltts
         */
        organizations: {
            /**
             * Method returns a list of organizations
             * @method get
             * @async
             * @memberof module:API.ltts.organizations
             * @returns {module:API.ltts.classes.Organization[]}
             * @throws {module:API.ltts.exceptions.PluginError}
             * @throws {module:API.ltts.exceptions.ServerError}
             */
            async get() {
                const result = await PluginRegistry.apiWrapper(ltts.organizations.get);
                return result;
            },
            /**
             * Method activates organization context
             * @method activate
             * @async
             * @param {module:API.ltts.classes.Organization}
             * @memberof module:API.ltts.organizations
             * @throws {module:API.ltts.exceptions.ArgumentError}
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async activate(organization) {
                const result = await PluginRegistry.apiWrapper(ltts.organizations.activate, organization);
                return result;
            },
            /**
             * Method deactivates organization context
             * @method deactivate
             * @async
             * @memberof module:API.ltts.organizations
             * @throws {module:API.ltts.exceptions.PluginError}
             */
            async deactivate() {
                const result = await PluginRegistry.apiWrapper(ltts.organizations.deactivate);
                return result;
            },
        },
        /**
         * Namespace is used for access to classes
         * @namespace classes
         * @memberof module:API.ltts
         */
        classes: {
            User,
            Project: implementProject(Project),
            Task,
            Job,
            Log,
            Attribute,
            Label,
            Statistics,
            ObjectState,
            MLModel,
            Comment,
            Issue,
            FrameData,
            CloudStorage,
            Organization,
        },
    };

    ltts.server = Object.freeze(ltts.server);
    ltts.projects = Object.freeze(ltts.projects);
    ltts.tasks = Object.freeze(ltts.tasks);
    ltts.jobs = Object.freeze(ltts.jobs);
    ltts.users = Object.freeze(ltts.users);
    ltts.plugins = Object.freeze(ltts.plugins);
    ltts.lambda = Object.freeze(ltts.lambda);
    ltts.client = Object.freeze(ltts.client);
    ltts.enums = Object.freeze(ltts.enums);
    ltts.cloudStorages = Object.freeze(ltts.cloudStorages);
    ltts.organizations = Object.freeze(ltts.organizations);

    const implementAPI = require('./api-implementation');

    Math.clamp = function clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    };

    const implemented = Object.freeze(implementAPI(ltts));
    return implemented;
}

module.exports = build();
